// cleanup_remove_external_libraries_GENERICS
// cleanup_convert_to_typescript_file_GENERICS

import React, { useMemo, useCallback, useReducer } from 'react';
import { uniqueId } from 'lodash';

import { Loading, NoContent, ActionGroup } from '@glu/grid-mobile-react';

import DataRow from './components/DataRow';
import DefinitionList from './components/DefinitionList';

const defaultReducer = (state = {}, newState) => ({ ...state, ...newState });

const getDefaultState = ({
  initialData = [],
  initialColumns = [],
  initialSort = {},
  initialFilters = {},
  initialBulkActionDefs = [],
  initialRowActionDefs = {},
  initialTotalRows = 0,
  initialPageSize = 50
} = {}) => ({
  isPaginating: false,
  loading: true,
  dataChanging: false,
  drawerOpen: false,
  drawer: null,
  data: initialData,
  columnState: initialColumns,
  sortState: initialSort,
  filterState: initialFilters,
  savedViewState: {},
  bulkActionDefs: initialBulkActionDefs,
  rowActionDefs: initialRowActionDefs,
  bulkActionState: null,
  selectedRows: [],
  pageSize: initialPageSize,
  page: 1,
  totalRows: initialTotalRows,
  enableSavedViews: false,
  defaultSavedView: undefined
});

const useMobileGrid = ({
  initialData = [],
  initialColumns = [],
  initialSort = {},
  initialFilters = {},
  initialBulkActionDefs = [],
  initialRowActionDefs = {},
  initialTotalRows = 0,
  initialPageSize = 50,
  gridReducer,
  initialState,
  forceOpenRows = []
} = {}) => {
  const [gridState, setGridState] = useReducer(
    gridReducer || defaultReducer,
    initialState ||
      getDefaultState({
        initialData,
        initialColumns,
        initialSort,
        initialFilters,
        initialBulkActionDefs,
        initialRowActionDefs,
        initialTotalRows,
        initialPageSize
      })
  );

  const {
    bulkActionState,
    columnState,
    data,
    dataChanging,
    loading,
    rowActionDefs,
    selectedRows
  } = gridState;

  // Column definitions
  const columnsWithoutActions = useMemo(
    () => columnState.filter((c) => c?.field !== 'actions'),
    [columnState]
  );
  const primaryColumns = useMemo(
    () => columnsWithoutActions.filter((c) => c?.primary && !c.hide),
    [columnsWithoutActions]
  );
  const sortColumns = useMemo(
    () => columnsWithoutActions.filter((c) => c?.sortable),
    [columnsWithoutActions]
  );
  const visibleColumns = useMemo(
    () => columnsWithoutActions.filter((c) => !c?.primary && !c?.hide),
    [columnsWithoutActions]
  );
  const filterColumns = useMemo(
    () => columnsWithoutActions.filter((c) => c?.filterable),
    [columnsWithoutActions]
  );

  // Drawer Actions
  const openDrawer = useCallback((newDrawer) => {
    setGridState({ drawerOpen: true, drawer: newDrawer });
  }, []);
  const closeDrawer = useCallback(() => setGridState({ drawerOpen: false }), []);

  // Grid Actions
  const onBulkActionSelect = useCallback(
    ({ rowId }) => {
      const newState = selectedRows.includes(rowId)
        ? selectedRows.filter((id) => id !== rowId)
        : selectedRows.concat(rowId);
      setGridState({ selectedRows: newState });
    },
    [selectedRows]
  );
  const onBulkActionSelectAll = useCallback(
    (checked) => setGridState({ selectedRows: checked ? data.map((d) => d.id) : [] }),
    [data]
  );
  const isSelectAllChecked = useMemo(
    () => data.every((d) => selectedRows.includes(d.id)),
    [selectedRows, data]
  );

  const gridContent = useMemo(() => {
    if (loading || dataChanging) {
      return <Loading primaryColumnLength={primaryColumns.length} />;
    }
    if (!data.length) {
      return <NoContent primaryColumnLength={primaryColumns.length} />;
    }
    return data.map((rowData, rowIndex) => {
      const id = rowData.id || uniqueId();
      const sortedActions =
        rowData?.actions?.length > 1
          ? [...rowData?.actions].sort((a, b) => (a?.toLowerCase() > b?.toLowerCase() ? 1 : -1))
          : [...rowData?.actions];
      const withSortedActionsRowData = { ...rowData, actions: sortedActions };
      return (
        <DataRow
          key={`data-row-${id}`}
          rowId={id}
          rowIndex={rowIndex}
          primaryColumns={primaryColumns}
          columns={visibleColumns}
          rowData={withSortedActionsRowData}
          bulkActionsEnabled={!!bulkActionState}
          isSelected={selectedRows.includes(id)}
          onBulkActionSelect={onBulkActionSelect}
          forceOpen={forceOpenRows.includes(id)}
        >
          <DefinitionList columns={visibleColumns} rowData={withSortedActionsRowData} rowId={id} />
          <ActionGroup
            rowData={withSortedActionsRowData}
            actionDefs={rowActionDefs}
            minNumberOfPrimaryRowActions={3}
          />
        </DataRow>
      );
    });
  }, [
    data,
    loading,
    primaryColumns,
    visibleColumns,
    rowActionDefs,
    bulkActionState,
    selectedRows,
    onBulkActionSelect,
    dataChanging,
    forceOpenRows
  ]);

  return useMemo(
    () => ({
      ...gridState,
      // grid content
      gridContent,
      // drawers
      closeDrawer,
      openDrawer,
      // bulk actions
      onBulkActionSelect,
      onBulkActionSelectAll,
      isSelectAllChecked,
      // columns
      columnsWithoutActions,
      primaryColumns,
      visibleColumns,
      sortColumns,
      filterColumns,
      setGridState
    }),
    [
      gridState,
      gridContent,
      isSelectAllChecked,
      closeDrawer,
      openDrawer,
      onBulkActionSelect,
      onBulkActionSelectAll,
      columnsWithoutActions,
      primaryColumns,
      visibleColumns,
      sortColumns,
      filterColumns,
      setGridState
    ]
  );
};

export default useMobileGrid;
